/**
 * Auth Provider Component
 *
 * Provides global authentication handling:
 * - Listens for session expiration events
 * - Shows toast notifications on auth errors
 * - Handles automatic logout on 401 responses
 *
 * Security: Uses HttpOnly cookies for JWT storage (XSS protection).
 * Token validation is handled by the backend on each request.
 */

import { useEffect, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore, AUTH_EVENTS, verifyAuth } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';

interface AuthProviderProps {
  children: ReactNode;
}

interface SessionExpiredDetail {
  message: string;
}

/**
 * AuthProvider wraps the app and handles global auth events
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { t } = useTranslation();
  const { addToast } = useUIStore();
  const { isAuthenticated, logout } = useAuthStore();

  /**
   * Handle session expired event - show toast
   */
  const handleSessionExpired = useCallback(
    (event: CustomEvent<SessionExpiredDetail>) => {
      const message = event.detail?.message || t('auth.sessionExpiredMessage');
      addToast('error', message);
    },
    [addToast, t]
  );

  /**
   * Verify session on app startup and when returning to tab
   * Since we use HttpOnly cookies, we verify by calling /api/auth/me
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    // Verify session is still valid on mount
    verifyAuth().catch(() => {
      // Auth verification failed, session expired
      addToast('error', t('auth.sessionExpiredMessage'));
      logout(false);
    });
  }, []); // Only run once on mount

  /**
   * Listen for global auth events
   */
  useEffect(() => {
    const onSessionExpired = (event: Event) => {
      handleSessionExpired(event as CustomEvent<SessionExpiredDetail>);
    };

    window.addEventListener(AUTH_EVENTS.SESSION_EXPIRED, onSessionExpired);

    return () => {
      window.removeEventListener(AUTH_EVENTS.SESSION_EXPIRED, onSessionExpired);
    };
  }, [handleSessionExpired]);

  /**
   * Handle visibility change - verify session when user returns to tab
   * Uses API call since we can't access HttpOnly cookies from JavaScript
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        // Verify session is still valid by calling the API
        verifyAuth().catch(() => {
          addToast('error', t('auth.sessionExpiredMessage'));
          logout(false);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, logout, addToast, t]);

  return <>{children}</>;
}

export default AuthProvider;
